import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import {
  archiveGroup as requestArchiveGroup,
  removeGroup as requestRemoveGroup,
  unarchiveGroup as requestUnarchiveGroup,
} from 'api/operations/groupService';
import MoveGroupModalStore from 'store/ui-stores/bi-dashboard/moveGroupModalStore';
import { RenewalAgentOfRecord } from 'types/groupService';
import { setUserOrgAffiliation } from 'api/operations/aaaService';
import { refreshToken } from 'api/refreshToken';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { ParentStore } from './searchAgencyDelegate';
import { BiGroupInteractionController, GroupAction, SelectedGroup } from './types';

interface ConstructorParams {
  setPageLoading: (status: boolean) => void;
  updatePageData: () => Promise<void>;
  parentStore: ParentStore;
}

export class GroupInteractionDelegate implements BiGroupInteractionController {
  private readonly _modals = {
    [GroupAction.REMOVE]: new ModalDelegate(),
    [GroupAction.ARCHIVE]: new ModalDelegate(),
    [GroupAction.MOVE]: new ModalDelegate(),
  };

  private _selectedGroup: SelectedGroup = { id: '', name: '', agencyId: '', aor: null };
  private readonly _rootStore: RootStore = getRootStoreInstance();

  private _selectGroup(id: string, name: string, agencyId: string, aor: RenewalAgentOfRecord | null): void {
    this._selectedGroup = { id, name, agencyId, aor };
  }

  private readonly _setPageLoading: (status: boolean) => void;
  private readonly _updatePageData: () => Promise<void>;

  public get selectedGroup(): SelectedGroup {
    return this._selectedGroup;
  }

  public get removeModal(): ModalDelegate {
    return this._modals[GroupAction.REMOVE];
  }

  public get archiveModal(): ModalDelegate {
    return this._modals[GroupAction.ARCHIVE];
  }

  public get moveModal(): ModalDelegate {
    return this._modals[GroupAction.MOVE];
  }

  public moveGroupModalUiStore;

  public commitGroupAction(
    groupAction: GroupAction,
    groupId: string,
    groupName: string,
    agencyId: string,
    aor: RenewalAgentOfRecord | null,
  ): void {
    this._selectGroup(groupId, groupName, agencyId, aor);
    this._modals[groupAction].open();
  }

  public closeActionModal(groupAction: GroupAction): void {
    this._modals[groupAction].close();
  }

  public async removeGroup(): Promise<void> {
    this.closeActionModal(GroupAction.REMOVE);
    this._setPageLoading(true);
    await requestRemoveGroup(this._selectedGroup.id);
    await this._updatePageData();
  }

  public async archiveGroup(): Promise<void> {
    this.closeActionModal(GroupAction.ARCHIVE);
    this._setPageLoading(true);
    await requestArchiveGroup(this._selectedGroup.id);
    await this._updatePageData();
  }

  public async unarchiveGroup(groupId: string): Promise<void> {
    this._setPageLoading(true);
    await requestUnarchiveGroup(groupId);
    await this._updatePageData();
  }

  public async viewGroupProfile(groupId: string, agencyId: string, agencyName: string): Promise<void> {
    this._setPageLoading(true);
    await setUserOrgAffiliation({
      sponsor: {
        id: this._rootStore.domainStoreV2.domainHeader.id,
        name: this._rootStore.domainStoreV2.domainHeader.name,
      },
      agency: {
        id: agencyId,
        name: agencyName,
      },
      subAgency: {},
    });

    await runInAction(async () => {
      try {
        await refreshToken();
        // Triggers header refresh when sponsor or agency details are updated
        window.location.href = `/group/${groupId}`;
      } catch (error) {
        window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
      }

      this._setPageLoading(false);
    });
  }

  public getSelectedGroup(): SelectedGroup {
    return this.selectedGroup;
  }

  constructor({ setPageLoading, updatePageData, parentStore }: ConstructorParams) {
    this._setPageLoading = setPageLoading;
    this._updatePageData = updatePageData;
    this.moveGroupModalUiStore = new MoveGroupModalStore({
      parentStore,
      getSelectedGroup: this.getSelectedGroup.bind(this),
      closeModal: this._modals[GroupAction.MOVE].close.bind(this._modals[GroupAction.MOVE]),
    });
    makeObservable<
      GroupInteractionDelegate,
      '_modals' | '_selectedGroup' | '_selectGroup' | '_setPageLoading' | '_updatePageData'
    >(this, {
      _modals: observable,
      _selectedGroup: observable,
      _selectGroup: action.bound,
      _setPageLoading: action,
      _updatePageData: action,
      archiveGroup: action.bound,
      unarchiveGroup: action.bound,
      removeGroup: action.bound,
      viewGroupProfile: action.bound,
      selectedGroup: computed,
      removeModal: computed,
      archiveModal: computed,
      closeActionModal: action.bound,
    });
  }
}
